import {
  Action,
  Alert,
  Attributes,
  Button,
  ButtonGroup,
  Card,
  openConfirmDialog,
  openDialog,
  Separator,
  showNotification,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isEmpty, isNil} from 'ramda';

import {
  TelephonyLine,
  TelephonyLineTwilioNumber,
  useDetachTelephonyLineNumberMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {buildArray, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AttachTelephonyNumberForm} from '../../../components/AttachTelephonyNumberForm/AttachTelephonyNumberForm';

interface TelephonyLineNumberCardProps extends RequiredTestIdProps {
  line: TelephonyLine | Nullish;
  isReadOnly: boolean;
}

export function TelephonyLineNumberCard(props: TelephonyLineNumberCardProps) {
  const [detachNumber] = useDetachTelephonyLineNumberMutation();

  const line = props.line;

  if (isNil(line)) {
    return null;
  }

  const handleConnect = () =>
    openDialog(
      <AttachTelephonyNumberForm
        lineId={line.id}
        data-testid={suffixTestId('attachNumber', props)}
      />,
      {
        id: 'attachTelephonyNumberDialog',
        title: i18n.t('page.settings.telephony.connectCardTitle'),
        size: 'small',
      }
    );

  const handleDisconnect = (number: TelephonyLineTwilioNumber) =>
    openConfirmDialog({
      text: i18n.t('page.settings.telephony.disconnectNumberConfirm'),
      'data-testid': suffixTestId('disconnectConfirm', props),
      onConfirm: () =>
        detachNumber({lineId: line.id, numberSid: number.sid})
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
          )
          .catch(handleApiError),
    });

  const actions = buildArray<Action>().whenNot(props.isReadOnly, {
    type: 'button',
    variant: 'secondary',
    title: i18n.t('page.settings.telephony.connectNumber'),
    onClick: handleConnect,
    'data-testid': suffixTestId('connect', props),
  });

  return (
    <Card
      title={i18n.t('page.settings.telephony.lineNumber')}
      actions={actions}
      data-testid={suffixTestId('numberCard', props)}
    >
      {isEmpty(line.twilioNumbers) ? (
        <Alert
          variant="info"
          message={i18n.t('page.settings.telephony.noNumberConnected')}
          data-testid={suffixTestId('noNumber', props)}
        />
      ) : (
        <VStack spacing={4}>
          {line.twilioNumbers.map((number, index) => (
            <VStack key={number.sid} spacing={4}>
              <Show when={index > 0}>
                <Separator spacing={0} />
              </Show>
              {match(number.lookupStatus)
                .with('ok', () => null)
                .with('missing', () => (
                  <Alert
                    variant="warning"
                    message={i18n.t('page.settings.telephony.numberMissingAtProvider')}
                    data-testid={suffixTestId(`numberMissing-[${number.sid}]`, props)}
                  />
                ))
                .with('unavailable', () => (
                  <Alert
                    variant="info"
                    message={i18n.t('page.settings.telephony.numberDataUnavailable')}
                    data-testid={suffixTestId(`numberUnavailable-[${number.sid}]`, props)}
                  />
                ))
                .exhaustive()}
              <Show when={number.lookupStatus !== 'missing' && isNil(number.lastInboundCallAt)}>
                <Alert
                  variant="warning"
                  message={i18n.t('page.settings.telephony.numberAwaitingFirstCall')}
                  data-testid={suffixTestId(`numberAwaitingFirstCall-[${number.sid}]`, props)}
                />
              </Show>
              <Attributes
                size="quarter"
                rows={[
                  {
                    label: i18n.t('entity.person.labels.phoneNumber'),
                    value: number.phoneNumber,
                  },
                  {
                    label: i18n.t('page.settings.telephony.numberSid'),
                    value: number.sid,
                  },
                ]}
                data-testid={suffixTestId(`numberAttributes-[${number.sid}]`, props)}
              />
              <Show when={!props.isReadOnly}>
                <ButtonGroup align="right">
                  <Button
                    variant="dangerLink"
                    title={i18n.t('page.settings.telephony.disconnectNumber')}
                    onClick={() => handleDisconnect(number)}
                    data-testid={suffixTestId(`disconnect-[${number.sid}]`, props)}
                  />
                </ButtonGroup>
              </Show>
            </VStack>
          ))}
        </VStack>
      )}
    </Card>
  );
}
